// University of Michigan: public college page (#2596), official facts only.
//
// Every figure below was read on 2026-09-30 from the source it names:
//  - Michigan's Common Data Set 2025-2026 (Office of Budget and Planning PDF):
//    C1 applicants/admits/enrollees for Fall 2025; C8 test policy for Fall
//    2027; C21/C22 early plans; G1/G5 2026-2027 costs. The C1 residency table
//    is left blank apart from its totals, so no in-state/out-of-state rows.
//    G1 labels the in-state rate "In-district"; G4 says 37% of full-time
//    undergraduates pay more than the G1 tuition (it varies by program and
//    by year of study).
//  - admissions.umich.edu: first-year application plans, requirements and
//    deadlines, and the "Application Changes" page for the fall 2027 cycle
//    (test-optional statement).
//  - College Scorecard, school id 170976 (net price by family income), read
//    from the Scorecard site's own API response.
// Nothing here is profile-derived or anyone's odds. Re-check a
// figure against its source before editing it, and move `lastChecked` when
// you do.

export const SOURCES = {
    cds: {
        label: 'Michigan Common Data Set 2025-2026',
        url: 'https://obp.umich.edu/wp-content/uploads/pubdata/cds/cds_2025-26_umaa.pdf',
        read: '2026-09-30',
    },
    plans: {
        label: 'Michigan Admissions, First-Year Application Plans',
        url: 'https://admissions.umich.edu/apply/first-year-applicants/first-year-application-plans',
        read: '2026-09-30',
    },
    requirements: {
        label: 'Michigan Admissions, Requirements and Deadlines',
        url: 'https://admissions.umich.edu/apply/first-year-applicants/requirements-deadlines',
        read: '2026-09-30',
    },
    changes: {
        label: 'Michigan Admissions, New for 2026-2027 Application Cycle',
        url: 'https://admissions.umich.edu/apply/first-year-applicants/requirements-deadlines/application-changes',
        read: '2026-09-30',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?170976-University-of-Michigan-Ann-Arbor',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-michigan-ann-arbor',
    kbId: 'university_of_michigan_ann_arbor',
    name: 'University of Michigan',
    shortName: 'Michigan',
    aliases: ['UMich', 'U of M', 'U-M'],
    location: 'Ann Arbor, MI',
    control: 'Public',
    lastChecked: '2026-09-30',
    sources: SOURCES,
    // From the KB's sourced branding (Michigan brand colors); decoration only.
    branding: {
        primary: '#FFCB05',
        secondary: '#00274C',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_michigan_ann_arbor_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 109112,
        admitted: 17915,
        enrolled: 8178,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Michigan\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major. Its residency table is blank apart from the totals, so it gives no in-state or out-of-state rate either.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early rounds',
        note: 'For Fall 2025 entry the Common Data Set reports no early decision plan and no early action counts, so it gives no early-round acceptance rate. Early decision is new since then (see the deadlines below).',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Some Michigan schools and colleges have their own application requirements on top of the Common App:',
        rows: [
            { school: 'Ross School of Business', requirement: 'An admissions portfolio, submitted with your application to U-M.' },
            { school: 'Taubman College of Architecture and Urban Planning', requirement: 'Additional requirements for Architecture majors only.' },
            { school: 'Penny W. Stamps School of Art and Design', requirement: 'Additional application requirements, set out on the Stamps site.' },
            { school: 'School of Music, Theatre and Dance', requirement: 'Additional application requirements, and its own December 1 application deadline.' },
            { school: 'Marsal Family School of Education (EPP and LEAPS)', requirement: 'Additional application requirements for the Educator Preparation Program and LEAPS.' },
        ],
        note: 'Michigan asks you to check each school\'s own site for the details.',
        source: 'requirements',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 1' },
        rows: [
            { plan: 'Early Decision', deadline: 'November 1', decision: 'By December 24', binding: true },
            { plan: 'Early Action', deadline: 'November 1', decision: 'By January 29', binding: false },
            { plan: 'Regular Decision', deadline: 'February 1', decision: 'By April 2', binding: false },
        ],
        note: 'School of Music, Theatre and Dance programs are Regular Decision only, with a December 1 deadline. Early decision admits pay the enrollment deposit by January 6; everyone else commits by May 1.',
        source: 'plans',
        binding: 'Early decision is binding. You, a parent or guardian, and your high school counselor sign the Early Decision Agreement, and if you are admitted you must immediately withdraw your applications to all other colleges. If you are postponed, you are no longer bound if admitted later.',
        bindingSource: 'plans',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Michigan stays test-optional for the fall 2027 cycle. The application asks whether you want the SAT or ACT considered: say yes and your scores are read; say no and they are not, even if Michigan already has them on file. You will not be disadvantaged without them.',
        source: 'changes',
        cdsLine: 'The Common Data Set marks the SAT or ACT as recommended for fall 2027 applicants, a stronger word than the admissions site\'s test-optional policy, with scores due by February 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-09-30',
        changes: [],
        source: 'plans',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition (first year)', amounts: [18402, 66602] },
            { item: 'Required fees', amounts: [494, 494] },
            { item: 'Food and housing (on campus)', amounts: [17220, 17220] },
            { item: 'Books and supplies', amounts: [1220, 1220] },
            { item: 'Transportation', amounts: [400, 400] },
            { item: 'Other expenses', amounts: [2458, 2458] },
        ],
        totals: [40194, 88394],
        note: 'Tuition varies by program and by year of study: the Common Data Set says 37% of full-time undergraduates pay more than the first-year rate shown.',
        source: 'cds',
        where: 'sections G1, G4 and G5',
        netPrice: {
            average: 14362,
            byIncome: [
                { income: '$0 to $30,000', amount: 3155 },
                { income: '$30,001 to $48,000', amount: 3372 },
                { income: '$48,001 to $75,000', amount: 5768 },
                { income: '$75,001 to $110,000', amount: 13888 },
                { income: 'Over $110,000', amount: 28625 },
            ],
            note: 'What first-year, full-time in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Out-of-state students pay more. Your own price comes from Michigan\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
